import React from 'react';
import { Link } from 'react-router-dom';
import { SectionCard } from '../../layout/memberPrimitives';
import type { ComingUpEvent } from '../../../utils/dashboard/comingUp';

/**
 * "Coming up" — the question patient portals most often fail to answer:
 * what happens next, and when.
 *
 * Every row has a real date. Undated plan cadence is filtered out upstream
 * rather than shown here as "date pending".
 *
 * An appointment stays on the list while it is happening, marked "Now" with a
 * Join button. It used to disappear the moment its start time passed, so a
 * member opening the dashboard at their appointment time saw "Nothing
 * scheduled" and had nowhere to find the link.
 */
export default function ComingUpList({
  events,
  onCancelBooking,
  cancellingId,
}: {
  events: ComingUpEvent[];
  /** Omit to hide cancellation (e.g. read-only contexts). */
  onCancelBooking?: (bookingId: string) => void;
  cancellingId?: string | null;
}) {
  if (events.length === 0) {
    return (
      <SectionCard eyebrow="Coming up" title="Nothing scheduled">
        <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          We&apos;ll write when your next test or review is booked.
        </p>
        <Link
          to="/book/clinical-history"
          className="text-sm font-medium"
          style={{ color: 'var(--melio-sage)' }}
        >
          Book your clinical history →
        </Link>
      </SectionCard>
    );
  }

  return (
    <SectionCard eyebrow="Coming up" title="What happens next">
      <ol className="list-none p-0 m-0">
        {events.map((event, index) => {
          const row = (
            <>
              <span
                className="text-xs tabular-nums shrink-0"
                style={{
                  color: event.isLive ? 'var(--melio-sage)' : 'var(--melio-text-muted)',
                  fontWeight: event.isLive ? 600 : 400,
                  width: 88,
                }}
              >
                {event.dateLabel}
              </span>
              <span className="min-w-0">
                <span className="text-sm block" style={{ color: 'var(--melio-ink)' }}>
                  {event.title}
                </span>
                <span className="text-[11px] block" style={{ color: 'var(--melio-text-muted)' }}>
                  {event.detail}
                </span>
              </span>
            </>
          );

          const canCancel = Boolean(event.bookingId && onCancelBooking && !event.isLive);

          return (
            <li
              key={event.id}
              className="py-2.5"
              style={{ borderTop: index === 0 ? 'none' : '1px solid var(--melio-border)' }}
            >
              <div className="flex items-baseline gap-3">
                {event.href ? (
                  <Link to={event.href} className="flex items-baseline gap-3 flex-1 min-w-0">
                    {row}
                  </Link>
                ) : (
                  <div className="flex items-baseline gap-3 flex-1 min-w-0">{row}</div>
                )}

                {event.isLive && event.joinHref && (
                  <a
                    href={event.joinHref}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-opacity hover:opacity-90"
                    style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
                  >
                    Join
                  </a>
                )}

                {canCancel && (
                  <button
                    type="button"
                    onClick={() => onCancelBooking!(event.bookingId!)}
                    disabled={cancellingId === event.bookingId}
                    className="shrink-0 text-xs underline disabled:opacity-50"
                    style={{ color: 'var(--melio-text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {cancellingId === event.bookingId ? 'Cancelling…' : 'Cancel'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}
